const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function getToken() {
  return localStorage.getItem('access_token');
}

// Authenticated request — handles token refresh on 401
export async function request(path, options = {}) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const res = await fetch(`${BASE}${path}`, { ...options, headers });

  if (res.status === 401) {
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      const refreshRes = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (refreshRes.ok) {
        const data = await refreshRes.json();
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        return request(path, options);
      } else {
        localStorage.clear();
        window.location.href = '/login';
      }
    }
  }

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

// Unauthenticated request — for public /getdb routes
async function publicRequest(path) {
  const res = await fetch(`${BASE}${path}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

// ── Auth ──────────────────────────────────────────────────────────────────────
export const login = (email, password) =>
  request('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });

export const signup = (email, password, username) =>
  request('/auth/signup', { method: 'POST', body: JSON.stringify({ email, password, username }) });

export const forgotPassword = (email) =>
  request('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) });

export const googleSync = (accessToken) =>
  request('/auth/google-sync', { method: 'POST', headers: { Authorization: `Bearer ${accessToken}` } });

export const getUserBadges = (userId) => request(`/db/users/${userId}/badges`);
export const getPublicUserBadges = (userId) => publicRequest(`/getdb/users/${userId}/badges`);

// ── Users (authenticated) ─────────────────────────────────────────────────────
export const getUsers = () => request('/db/users');
export const getUser = (id) => request(`/db/users/${id}`);
export const updateUser = (id, updates) =>
  request(`/db/users/${id}`, { method: 'PUT', body: JSON.stringify(updates) });

// Avatar upload — multipart/form-data, browser sets Content-Type with boundary
export const uploadAvatar = async (userId, file) => {
  const token = getToken();
  const formData = new FormData();
  formData.append('avatar', file);
  const res = await fetch(`${BASE}/api/users/${userId}/avatar`, {
    method: 'POST',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: formData,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Upload failed');
  return data;
};

// ── Courses (authenticated) ───────────────────────────────────────────────────
export const getCourses = () => request('/db/courses');
export const getCourse = (id) => request(`/db/courses/${id}`);
export const getCourseTees = (id) => request(`/db/courses/${id}/tees`);
export const postCourse = (data) =>
  request('/db/courses', { method: 'POST', body: JSON.stringify(data) });

// ── Courses (public, no auth) ─────────────────────────────────────────────────
export const getPublicCourses = () => publicRequest('/getdb/courses');
export const getPublicCourse = (id) => publicRequest(`/getdb/courses/${id}`);
export const getPublicCourseTees = (id) => publicRequest(`/getdb/courses/${id}/tees`);
export const getPublicTee = (id) => publicRequest(`/getdb/tees/${id}`);
export const getPublicCourseLeaderboard = (courseId) => publicRequest(`/getdb/leaderboard/course/${courseId}`);
export const getPublicCourseLeaderboardMonthly = (courseId, year, month) =>
  publicRequest(`/getdb/leaderboard/course/${courseId}/monthly?year=${year}&month=${month}`);
export const getPublicTeeLeaderboard = (teeId) => publicRequest(`/getdb/leaderboard/tee/${teeId}`);
export const getPublicTeeLeaderboardMonthly = (teeId, year, month) =>
  publicRequest(`/getdb/leaderboard/tee/${teeId}/monthly?year=${year}&month=${month}`);

// ── Golf API Search ───────────────────────────────────────────────────────────
export const searchGolfCourses = (course, city = '', state = '', country = '') =>
  request(`/api/golf-courses?course=${encodeURIComponent(course)}&city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}&country=${encodeURIComponent(country)}`);

export const getGolfCourseDetails = (courseId) =>
  request(`/api/golf-courses/${courseId}`);

// ── Tees ──────────────────────────────────────────────────────────────────────
export const getTee = (id) => request(`/db/tees/${id}`);

// ── Leaderboard ───────────────────────────────────────────────────────────────
export const getCourseLeaderboard = (courseId) => request(`/db/leaderboard/course/${courseId}`);
export const getCourseLeaderboardMonthly = (courseId, year, month) =>
  request(`/db/leaderboard/course/${courseId}/monthly?year=${year}&month=${month}`);
export const getTeeLeaderboard = (teeId) => request(`/db/leaderboard/tee/${teeId}`);
export const getTeeLeaderboardMonthly = (teeId, year, month) =>
  request(`/db/leaderboard/tee/${teeId}/monthly?year=${year}&month=${month}`);

// ── Local Legends (Regional Leaderboard) ─────────────────────────────────────
// period: 'alltime' | 'year' | 'month'
// year and month are only required when period is 'year' or 'month' respectively
export const getLocalLegends = (regionType, regionVal, period = 'alltime', year, month) => {
  let path = `/db/local-legends/${regionType}/${encodeURIComponent(regionVal)}?period=${period}`;
  if (period !== 'alltime' && year) path += `&year=${year}`;
  if (period === 'month' && month) path += `&month=${month}`;
  return request(path);
};

export const getPublicLocalLegends = (regionType, regionVal, period = 'alltime', year, month) => {
  let path = `/getdb/local-legends/${regionType}/${encodeURIComponent(regionVal)}?period=${period}`;
  if (period !== 'alltime' && year) path += `&year=${year}`;
  if (period === 'month' && month) path += `&month=${month}`;
  return publicRequest(path);
};

// ── Scores ────────────────────────────────────────────────────────────────────
export const postScore = (data) =>
  request('/db/scores', { method: 'POST', body: JSON.stringify(data) });

export const getPendingFriendPosts = () =>
  request('/db/scores/pending-friend-posts');

export const respondToFriendPost = (score_id, action) =>
  request('/db/scores/friend-post/respond', { method: 'POST', body: JSON.stringify({ score_id, action }) });

export const deleteScore = (id) =>
  request(`/db/scores/${id}`, { method: 'DELETE' });

// ── Score Verification ────────────────────────────────────────────────────────
export const verifyScore = (score_id) =>
  request('/db/scores/verify', { method: 'POST', body: JSON.stringify({ score_id }) });

export const getScoreVerification = (scoreId) =>
  request(`/db/scores/${scoreId}/verification`);

// ── Score Reporting ───────────────────────────────────────────────────────────
export const reportScore = (score_id) =>
  request('/db/scores/report', { method: 'POST', body: JSON.stringify({ score_id }) });

export const getScoreReports = (scoreId) =>
  request(`/db/scores/${scoreId}/reports`);

// ── Issue Reporting ───────────────────────────────────────────────────────────
export const reportIssue = (description) =>
  request('/db/issues', { method: 'POST', body: JSON.stringify({ description }) });

// ── Friends ───────────────────────────────────────────────────────────────────
export const getFriends = () => request('/db/friends');
export const sendFriendRequest = (addressee_id) =>
  request('/db/friends/request', { method: 'POST', body: JSON.stringify({ addressee_id }) });
export const respondToFriendRequest = (requester_id, action) =>
  request('/db/friends/respond', { method: 'POST', body: JSON.stringify({ requester_id, action }) });
export const removeFriend = (userId) =>
  request(`/db/friends/${userId}`, { method: 'DELETE' });

// ── Friends leaderboards ──────────────────────────────────────────────────────
export const getFriendsCourseLeaderboard = (courseId) =>
  request(`/db/leaderboard/course/${courseId}/friends`);
export const getFriendsTeeLeaderboard = (teeId) =>
  request(`/db/leaderboard/tee/${teeId}/friends`);